import { type QueryClient } from '@tanstack/react-query';

import { type TestFamilyQuery } from '@/modules/germination-test/services/germination-api';
import { type ToleranceTableQuery } from '@/modules/germination-test/services/germination-catalogs-api';
import { type InstallationCatalog } from '@/modules/installation/services/installation-catalogs-api';
import {
  type InstallationListParams,
  type InstallationSource,
} from '@/modules/installation/services/installation-types';
import { type AnalysisListParams } from '@/modules/lab-shared/services/analysis-schemas';

export const analystKeys = {
  all: () => ['analysts'] as const,
  lists: () => [...analystKeys.all(), 'list'] as const,
  list: (name?: string) => [...analystKeys.lists(), name ?? ''] as const,
};

export const imageKeys = {
  all: () => ['images'] as const,
  lists: () => [...imageKeys.all(), 'list'] as const,
  list: (analysisId: string) => [...imageKeys.lists(), analysisId] as const,
};

export const producerKeys = {
  all: () => ['producers'] as const,
  lists: () => [...producerKeys.all(), 'list'] as const,
  list: (search?: string) => [...producerKeys.lists(), search ?? ''] as const,
};

export const tetrazoliumKeys = {
  all: () => ['tetrazolium'] as const,
  lists: () => [...tetrazoliumKeys.all(), 'list'] as const,
  list: (params: AnalysisListParams) => [...tetrazoliumKeys.lists(), params] as const,
  details: () => [...tetrazoliumKeys.all(), 'detail'] as const,
  detail: (id: string, produtorId: string) =>
    [...tetrazoliumKeys.details(), id, produtorId] as const,
  settings: () => [...tetrazoliumKeys.all(), 'settings'] as const,
  grades: () => [...tetrazoliumKeys.settings(), 'grades'] as const,
  damages: () => [...tetrazoliumKeys.settings(), 'damages'] as const,
};

export const sandBedKeys = {
  all: () => ['sand-bed'] as const,
  lists: () => [...sandBedKeys.all(), 'list'] as const,
  list: (params: AnalysisListParams) => [...sandBedKeys.lists(), params] as const,
  details: () => [...sandBedKeys.all(), 'detail'] as const,
  detail: (id: string, produtorId: string) => [...sandBedKeys.details(), id, produtorId] as const,
};

export const installationKeys = {
  all: () => ['installation'] as const,
  lists: () => [...installationKeys.all(), 'list'] as const,
  list: (params: InstallationListParams) => [...installationKeys.lists(), params] as const,
  details: () => [...installationKeys.all(), 'detail'] as const,
  detail: (id: string, produtorId: string, source: InstallationSource) =>
    [...installationKeys.details(), id, produtorId, source] as const,
  catalogs: () => [...installationKeys.all(), 'catalog'] as const,
  catalog: (catalog: InstallationCatalog) => [...installationKeys.catalogs(), catalog] as const,
};

export const germinationKeys = {
  all: () => ['germination'] as const,
  details: () => [...germinationKeys.all(), 'detail'] as const,
  detail: (id: string, produtorId: string) =>
    [...germinationKeys.details(), id, produtorId] as const,
  families: () => [...germinationKeys.all(), 'family'] as const,
  family: (query: TestFamilyQuery, produtorId: string) =>
    [...germinationKeys.families(), query, produtorId] as const,
  catalogs: () => [...germinationKeys.all(), 'catalog'] as const,
  abnormalities: () => [...germinationKeys.catalogs(), 'abnormalities'] as const,
  determinations: (testKind: string) =>
    [...germinationKeys.catalogs(), 'determinations', testKind] as const,
  tolerance: (query: ToleranceTableQuery) =>
    [...germinationKeys.catalogs(), 'tolerance', query] as const,
};

export const labSettingsKeys = {
  all: () => ['lab-settings'] as const,
};

export const invalidateImages = {
  list: (queryClient: QueryClient, analysisId: string) =>
    queryClient.invalidateQueries({ queryKey: imageKeys.list(analysisId) }),
};

export const invalidateTetrazolium = {
  all: (queryClient: QueryClient) =>
    queryClient.invalidateQueries({ queryKey: tetrazoliumKeys.all() }),
  lists: (queryClient: QueryClient) =>
    queryClient.invalidateQueries({ queryKey: tetrazoliumKeys.lists() }),
  detail: (queryClient: QueryClient, id: string) =>
    queryClient.invalidateQueries({ queryKey: [...tetrazoliumKeys.details(), id] }),
};

export const invalidateSandBed = {
  all: (queryClient: QueryClient) => queryClient.invalidateQueries({ queryKey: sandBedKeys.all() }),
  lists: (queryClient: QueryClient) =>
    queryClient.invalidateQueries({ queryKey: sandBedKeys.lists() }),
  detail: (queryClient: QueryClient, id: string) =>
    queryClient.invalidateQueries({ queryKey: [...sandBedKeys.details(), id] }),
};

export const invalidateInstallation = {
  lists: (queryClient: QueryClient) =>
    queryClient.invalidateQueries({ queryKey: installationKeys.lists() }),
  detail: (queryClient: QueryClient, id: string) =>
    queryClient.invalidateQueries({ queryKey: [...installationKeys.details(), id] }),
};

export const invalidateGermination = {
  all: (queryClient: QueryClient) =>
    queryClient.invalidateQueries({ queryKey: germinationKeys.all() }),
  detail: (queryClient: QueryClient, id: string) =>
    queryClient.invalidateQueries({ queryKey: [...germinationKeys.details(), id] }),
  families: (queryClient: QueryClient) =>
    queryClient.invalidateQueries({ queryKey: germinationKeys.families() }),
};
